// Client Book backfill upload responses (SEP-352 Stage 6, ADR-0015 D8).
//
// The three re-runnable admin ingests (contacts, mapping, KYC) return the same
// envelope: per-run counts plus the exception + conflict lists the IM/EA review
// loop reads (answer #3). camelCase throughout (web↔server wire).
//
// This envelope used to be the ONLY home of a run's report, on the reasoning
// that exceptions are per-run data and never Object Types. SEP-195 supersedes
// that: the drawer prints the counts and drops the detail, so every miss on
// these doors was silent. The same report is now ALSO written to `IngestRun`
// and `IngestRunException` (ADR-0009), and the Administration page reads it
// back. This schema stays the immediate response of the upload.
//
// These endpoints are multipart, so they sit OUTSIDE ts-rest (like the other
// admin uploads); this schema is the shared response contract the web summary
// renders against.

import { z } from 'zod';

/** One row the ingest could not place fully — reported, never silently
 *  dropped. On the contacts door the row IS imported and the exception names
 *  the missing link (SEP-195); on the other doors the row is not imported. */
export const backfillExceptionSchema = z.object({
    /** A short machine reason, e.g. 'unresolved_ref', 'name_collision',
     *  'missing_sur_number', 'invalid_sur_number', 'duplicate_sur_in_file',
     *  'sur_conflict', 'no_owner', 'missing_crm_id', 'missing_name'. */
    reason: z.string(),
    /** Human-readable detail for the review loop. */
    detail: z.string(),
    /** The source row's identifying reference, when known (SUR/CRM id/ref). */
    ref: z.string().nullable().optional(),
});
export type BackfillException = z.infer<typeof backfillExceptionSchema>;

/** One descriptive field the file would have changed but a human had frozen
 *  (per-field provenance, ADR-0015 D8a) — applied nowhere, reported here. */
export const backfillConflictSchema = z.object({
    /** The target row (Contact/SUR) id. */
    rowId: z.string(),
    /** The camelCase descriptive field name that was protected. */
    field: z.string(),
    /** What the file wanted to write (stringified for display). */
    fileValue: z.string().nullable(),
    /** What was kept (the human-edited value, stringified). */
    keptValue: z.string().nullable(),
});
export type BackfillConflict = z.infer<typeof backfillConflictSchema>;

/** The shared per-run report every backfill ingest returns. */
export const backfillReportSchema = z.object({
    ok: z.literal(true),
    parsed: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    /** Rows that landed with no link and no owner (SEP-195). They ARE imported,
     *  so they are not part of `skipped`. Optional, because only the contacts
     *  door reports it today. */
    unlinked: z.number().int().nonnegative().optional(),
    exceptions: z.array(backfillExceptionSchema),
    conflicts: z.array(backfillConflictSchema),
});
export type BackfillReport = z.infer<typeof backfillReportSchema>;
